import SizeEnum from '@models/enums/SizeEnum';
import TagTypesEnum from '@models/enums/TagTypesEnum';
import type { ILink } from '@models/ILink';
import classNames from 'classnames';
import type {
  ButtonHTMLAttributes,
  DetailedHTMLProps,
  PropsWithChildren,
} from 'react';

interface Props {
  readonly className?: string;
  readonly disabled?: boolean;
  readonly editHint?: string;
  readonly link?: ILink;
  readonly linkCallback?: () => void;
  readonly pointerStates?: boolean;
  readonly size?: Exclude<SizeEnum, SizeEnum.XLarge | SizeEnum.Medium>;
  readonly tagType?: TagTypesEnum;
  readonly text?: string;
  readonly span?: boolean;
  readonly isLightMode?: boolean;
  readonly dataTestId?: string;
}

type TagProps = Props &
  DetailedHTMLProps<ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement>;

const Tag = ({
  children,
  className,
  disabled = false,
  editHint,
  link,
  linkCallback,
  pointerStates = true,
  size = SizeEnum.Small,
  span = false,
  tagType,
  text,
  isLightMode,
  dataTestId,
  ...buttonProps
}: PropsWithChildren<TagProps>) => {
  const tagClasses = classNames(
    {
      [`text-black-100 bg-grey-200 ${
        isLightMode ? '' : 'dark:bg-grey-600 dark:text-white'
      }`]: tagType === TagTypesEnum.Primary,
      //
      [`hover:bg-grey active:text-white active:bg-black-100 ${
        isLightMode
          ? ''
          : 'dark:hover:text-white dark:hover:bg-grey-500 dark:active:text-black-100 dark:active:bg-white'
      }`]: tagType === TagTypesEnum.Primary && pointerStates,
      //
      [`text-white bg-black-100 ${
        isLightMode ? '' : 'dark:bg-white dark:text-black-100'
      }`]:
        tagType === TagTypesEnum.Secondary || tagType === TagTypesEnum.Square,
      //
      [`hover:bg-grey hover:text-black-100 active:text-black-100 active:bg-grey-200 ${
        isLightMode
          ? ''
          : 'dark:hover:text-white dark:hover:bg-grey-500 dark:active:text-white dark:active:bg-grey-600'
      }`]: tagType === TagTypesEnum.Secondary && pointerStates,
      //
      [`text-black-100 bg-white ${
        isLightMode ? '' : 'dark:bg-black-100 dark:text-white'
      }`]: tagType === TagTypesEnum.Three,
      //
      [`hover:bg-grey active:bg-grey-400 ${
        isLightMode
          ? ''
          : 'dark:hover:text-white dark:hover:bg-grey-500 dark:active:text-black-100 dark:active:bg-white'
      }`]:
        (tagType === TagTypesEnum.Three || tagType === TagTypesEnum.Five) &&
        pointerStates,
      //
      'text-yellow bg-black-100': tagType === TagTypesEnum.Four,
      //
      'hover:bg-grey hover:text-black-100 active:text-black-100 active:bg-grey-100':
        tagType === TagTypesEnum.Four && pointerStates,
      //
      [`text-black-100 bg-grey-100 ${
        isLightMode ? '' : 'dark:bg-grey-600 dark:text-white'
      }`]: tagType === TagTypesEnum.Five,
      //
      'py-2.5 px-5': size === SizeEnum.Large && tagType !== TagTypesEnum.Square,
      //
      'px-4 py-1': size === SizeEnum.Small && tagType !== TagTypesEnum.Square,
      //
      'pointer-events-none': !pointerStates,
    },
    tagType === TagTypesEnum.Square ? 'rounded-[3px]' : 'rounded-full body-two',
    'select-none',
    className
  );

  if (span) {
    return (
      <span className={tagClasses} data-testid={dataTestId}>
        {text}
      </span>
    );
  }

  return link?.url && (link?.name || children) && !disabled ? (
    <a
      className={classNames('appearance-none', tagClasses)}
      href={link.url}
      target={link.target}
      data-epi-property-name={editHint}
      onClick={linkCallback}
      data-testid={dataTestId}
    >
      {children || link.name}
    </a>
  ) : (
    <button
      {...buttonProps}
      className={tagClasses}
      disabled={disabled}
      data-epi-property-name={editHint}
      data-testid={dataTestId}
    >
      {children || text}
    </button>
  );
};

export default Tag;
